import { ScannerButton, ScannerDialog, useScannerConfiguration } from './Scanner';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as qz from 'qz-tray';
import { PDFDocument, degrees } from 'pdf-lib';
import PrintIcon from '@mui/icons-material/Print';
import { Alert, Box, Button, Checkbox, Divider, FormControlLabel, Grid, MenuItem, Snackbar, Switch, TextField, Typography } from '@mui/material';
import { FormikProps, getIn, setIn } from 'formik';
import { CardType, ContentCard } from 'src/components/organisms/ContentCard';
import { useSettingsContext } from 'src/hooks/useSettings';
import WarehouseSelector, { ACTIVE_WAREHOUSE_SETTING } from 'src/components/atoms/WarehouseSelector';
import SettingsLabel from 'src/components/atoms/form/SettingsLabel';
import SettingContainer from 'src/components/atoms/form/SettingsContainer';
import { useGetPickingListByDispatchOrder, useGetCollectivePickingList, usePrintProductLabel } from 'src/hooks/api/requestConfigs/warehouse';

export type PrinterSettings = {
    printer: string;
    pageSize: 'printer' | 'a4' | 'a5' | 'letter' | 'label' | 'custom';
    widthMm: number;
    heightMm: number;
};

export type LabelPrinterSettings = PrinterSettings & {
    mode: 'pdf' | 'zebra';
    zebraOptions: string;
    zebraCommands: string;
    densityDpi: number;
    stretchToFit: boolean;
};

export type PrintingSection = 'pickingList' | 'relocationOrder' | 'deliveryNoteDocument' | 'deliveryNote' | 'productLabel' | 'priceTag';

export type DispatchPrintingSettings = {
    fallbackMethod: 'savePdf' | 'skip';
    useCertificates: boolean;
    pickingList: PrinterSettings & { delayMs: number; refreshMs: number };
    relocationOrder: PrinterSettings;
    deliveryNoteDocument: PrinterSettings;
    deliveryNote: LabelPrinterSettings;
    productLabel: LabelPrinterSettings;
    priceTag: LabelPrinterSettings;
};

const DEFAULT_PRINTER: PrinterSettings = { printer: '', pageSize: 'a4', widthMm: 210, heightMm: 297 };

// Keep the existing address-label key and Zebra setup for saved settings.
export const DEFAULT_ADDRESS_LABEL_PRINTER: LabelPrinterSettings = {
    printer: '', pageSize: 'label', widthMm: 100, heightMm: 150, mode: 'pdf',
    zebraOptions: '{"pageWidth":850,"pageHeight":4200}', zebraCommands: '^PW864\n^LL2006\n^LH16,20\n^PR5\n^MD22\n^PQ1', densityDpi: 203, stretchToFit: false,
};

// Product-label Zebra defaults from the printer settings screenshot.
export const DEFAULT_PRODUCT_LABEL_PRINTER: LabelPrinterSettings = {
    printer: 'ZDesigner GK420t', pageSize: 'custom', widthMm: 50, heightMm: 30, mode: 'zebra',
    zebraOptions: '{"pageWidth":980,"pageHeight":3000}', zebraCommands: '^PW590\n^LL120\n^LH20,0\n^PR5\n^MD20\n^LT-30\n^PQ1', densityDpi: 203, stretchToFit: false,
};

// Price-tag screenshot defaults, with a 5:4 rendering size for independent stretching.
export const DEFAULT_PRICE_TAG_PRINTER: LabelPrinterSettings = {
    printer: 'ZDesigner GK420t', pageSize: 'custom', widthMm: 45, heightMm: 27, mode: 'zebra',
    zebraOptions: '{"pageWidth":640,"pageHeight":1300}', zebraCommands: '^PW317\n^LH30,17\n^PR5\n^MD20\n^PQ1', densityDpi: 203, stretchToFit: true,
};

export const DEFAULT_DISPATCH_PRINTING: DispatchPrintingSettings = {
    fallbackMethod: 'savePdf',
    useCertificates: true,
    pickingList: { ...DEFAULT_PRINTER, delayMs: 3000, refreshMs: 60000 },
    relocationOrder: { ...DEFAULT_PRINTER },
    deliveryNoteDocument: { ...DEFAULT_PRINTER },
    deliveryNote: { ...DEFAULT_ADDRESS_LABEL_PRINTER },
    productLabel: { ...DEFAULT_PRODUCT_LABEL_PRINTER },
    priceTag: { ...DEFAULT_PRICE_TAG_PRINTER },
};

const asBoolean = (value: unknown, fallback: boolean) =>
    value === true || value === 'true' ? true : value === false || value === 'false' ? false : fallback;

const asNumber = (value: unknown, fallback: number, minimum: number) =>
    value !== '' && value != null && Number.isFinite(Number(value)) && Number(value) >= minimum
        ? Number(value) : fallback;

const normalizePrinter = (value: any, defaults: PrinterSettings = DEFAULT_PRINTER): PrinterSettings => ({
    printer: typeof value?.printer === 'string' ? value.printer : defaults.printer,
    pageSize: ['printer', 'a4', 'a5', 'letter', 'label', 'custom'].includes(value?.pageSize) ? value.pageSize : defaults.pageSize,
    widthMm: asNumber(value?.widthMm, defaults.widthMm, 1),
    heightMm: asNumber(value?.heightMm, defaults.heightMm, 1),
});

const normalizeLabelPrinter = (value: any, defaults: LabelPrinterSettings): LabelPrinterSettings => ({
    ...normalizePrinter(value, defaults),
    mode: value?.mode === 'pdf' || value?.mode === 'zebra' ? value.mode : defaults.mode,
    zebraOptions: typeof value?.zebraOptions === 'string' ? value.zebraOptions : defaults.zebraOptions,
    zebraCommands: typeof value?.zebraCommands === 'string' ? value.zebraCommands : defaults.zebraCommands,
    densityDpi: asNumber(value?.densityDpi, defaults.densityDpi, 1),
    stretchToFit: asBoolean(value?.stretchToFit, defaults.stretchToFit),
});

/** Merge defaults at each level; no settings are written just by opening a page. */
export const getDispatchPrintingSettings = (settings: any): DispatchPrintingSettings => {
    const value = settings?.DispatchPrinting;
    const defaults = DEFAULT_DISPATCH_PRINTING;
    return {
        fallbackMethod: value?.fallbackMethod === 'skip' ? 'skip' : defaults.fallbackMethod,
        useCertificates: asBoolean(value?.useCertificates, defaults.useCertificates),
        pickingList: {
            ...normalizePrinter(value?.pickingList),
            delayMs: Math.min(asNumber(value?.pickingList?.delayMs, defaults.pickingList.delayMs, 0), 3600000),
            refreshMs: Math.min(asNumber(value?.pickingList?.refreshMs, defaults.pickingList.refreshMs, 1000), 86400000),
        },
        relocationOrder: normalizePrinter(value?.relocationOrder, defaults.relocationOrder),
        deliveryNoteDocument: normalizePrinter(value?.deliveryNoteDocument, defaults.deliveryNoteDocument),
        deliveryNote: normalizeLabelPrinter(value?.deliveryNote, defaults.deliveryNote),
        productLabel: normalizeLabelPrinter(value?.productLabel, defaults.productLabel),
        priceTag: normalizeLabelPrinter(value?.priceTag, defaults.priceTag),
    };
};

export const useDispatchPrintingSettings = () => {
    const { settings } = useSettingsContext();
    return useMemo(() => getDispatchPrintingSettings(settings), [settings]);
};

export const parseZebraOptions = (text: string): Record<string, unknown> => {
    let options: any;
    try { options = JSON.parse(text.trim() || '{}'); } catch { throw new Error('Zebra options must be a valid JSON object.'); }
    if (!options || typeof options !== 'object' || Array.isArray(options)) throw new Error('Zebra options must be a JSON object.');
    if (options.language != null && options.language !== 'ZPL') throw new Error('Zebra mode requires language "ZPL".');
    return options;
};

// QZ raw PDF rendering fits proportionally. Reshape the PDF first to support independent axes.
const getZebraStretchSize = (options: Record<string, unknown>) => {
    const width = options.pageWidth;
    const height = options.pageHeight;
    if (typeof width !== 'number' || typeof height !== 'number' || !Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) throw new Error('Stretching requires positive numeric pageWidth and pageHeight in Zebra PDF options.');
    return { width, height };
};

const stretchZebraPdf = async (blob: Blob, options: Record<string, unknown>): Promise<string> => {
    const { width, height } = getZebraStretchSize(options);
    const source = await PDFDocument.load(await blob.arrayBuffer());
    const output = await PDFDocument.create();
    for (const page of source.getPages()) {
        const crop = page.getCropBox();
        const embedded = await output.embedPage(page, { left: crop.x, bottom: crop.y, right: crop.x + crop.width, top: crop.y + crop.height });
        const rotation = ((page.getRotation().angle % 360) + 360) % 360;
        const sideways = rotation === 90 || rotation === 270;
        const x = rotation === 180 || rotation === 270 ? width : 0;
        const y = rotation === 90 || rotation === 180 ? height : 0;
        output.addPage([width, height]).drawPage(embedded, { x, y, width: sideways ? height : width, height: sideways ? width : height, rotate: degrees(-rotation) });
    }
    return output.saveAsBase64();
};

export const validateDispatchPrintingSettings = (values: any) => {
    let errors = {};
    const checkNumber = (path: string, minimum: number, maximum = Number.MAX_SAFE_INTEGER) => {
        const value = getIn(values, `DispatchPrinting.${path}`);
        if (value === '' || value == null || !Number.isFinite(Number(value)) || Number(value) < minimum || Number(value) > maximum) {
            errors = setIn(errors, `DispatchPrinting.${path}`, `Enter a number from ${minimum} to ${maximum}.`);
        }
    };
    checkNumber('pickingList.delayMs', 0, 3600000);
    checkNumber('pickingList.refreshMs', 1000, 86400000);
    const normalized = getDispatchPrintingSettings(values);
    (['pickingList', 'relocationOrder', 'deliveryNoteDocument', 'deliveryNote', 'productLabel', 'priceTag'] as const).forEach((section) => {
        const label = section === 'deliveryNote' || section === 'productLabel' || section === 'priceTag' ? normalized[section] : null;
        const zebra = label?.mode === 'zebra';
        if (!zebra && getIn(values, `DispatchPrinting.${section}.pageSize`) === 'custom') {
            checkNumber(`${section}.widthMm`, 1, 2000);
            checkNumber(`${section}.heightMm`, 1, 2000);
        }
        if (zebra) {
            // A newly introduced section can be absent from older Formik values.
            if (getIn(values, `DispatchPrinting.${section}`) == null) return;
            checkNumber(`${section}.densityDpi`, 1, 2400);
            try {
                const options = parseZebraOptions(getIn(values, `DispatchPrinting.${section}.zebraOptions`) ?? label!.zebraOptions);
                if (label!.stretchToFit) getZebraStretchSize(options);
            }
            catch (error) { errors = setIn(errors, `DispatchPrinting.${section}.zebraOptions`, errorMessage(error)); }
        }
    });
    return errors;
};

const errorMessage = (error: unknown) => error instanceof Error ? error.message : String(error);

// All discovery and print operations share this queue. Security callbacks are
// global in QZ, so changing certificate mode must wait for the current job.
let printerQueue: Promise<void> = Promise.resolve();
const queuePrinterOperation = <T,>(operation: () => Promise<T>): Promise<T> => {
    const result = printerQueue.then(operation);
    printerQueue = result.then(() => undefined, () => undefined);
    return result;
};

let certificateMode: boolean | undefined;
let privateKeyPromise: Promise<CryptoKey> | null = null;

const fetchTextFile = async (url: string) => {
    const response = await fetch(url, { cache: 'no-store' });
    if (!response.ok) throw new Error(`Failed to load ${url}: ${response.status}`);
    return response.text();
};

const bytesToBase64 = (bytes: Uint8Array) => {
    let binary = '';
    bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
    return window.btoa(binary);
};

const getPrivateKey = () => {
    if (!privateKeyPromise) {
        // Reuse the application's existing certificate files and PKCS#8 signer.
        privateKeyPromise = fetchTextFile('/qz/private-key.pem').then((pem) => {
            const binary = window.atob(pem.replace(/-----BEGIN PRIVATE KEY-----|-----END PRIVATE KEY-----|\s/g, ''));
            return window.crypto.subtle.importKey('pkcs8', Uint8Array.from(binary, (char) => char.charCodeAt(0)),
                { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-512' }, false, ['sign']);
        }).catch((error) => { privateKeyPromise = null; throw error; });
    }
    return privateKeyPromise;
};

const ensureQzConnected = async (useCertificates: boolean) => {
    if (certificateMode !== useCertificates && qz.websocket.isActive()) await qz.websocket.disconnect();
    // Reapply both handlers in case another QZ page replaced them.
    if (useCertificates) {
        qz.security.setCertificatePromise((resolve, reject) => {
            fetchTextFile('/qz/digital-certificate.txt').then(resolve, reject);
        }, { rejectOnFailure: true });
        qz.security.setSignatureAlgorithm('SHA512');
        qz.security.setSignaturePromise((toSign) => (resolve, reject) => {
            getPrivateKey().then((key) => window.crypto.subtle.sign('RSASSA-PKCS1-v1_5', key, new TextEncoder().encode(toSign)))
                .then((signature) => resolve(bytesToBase64(new Uint8Array(signature))), reject);
        });
    } else {
        qz.security.setCertificatePromise((resolve) => resolve(''));
        qz.security.setSignaturePromise(() => (resolve) => resolve(''));
    }
    certificateMode = useCertificates;
    if (!qz.websocket.isActive()) await qz.websocket.connect({ retries: 2, delay: 1 });
};

const findPrinters = async (): Promise<string[]> => {
    const found = await qz.printers.find();
    return Array.from(new Set<string>((Array.isArray(found) ? found : [found])
        .filter((printer) => typeof printer === 'string' && printer.length > 0))).sort();
};

export const loadDispatchPrinters = (useCertificates: boolean): Promise<string[]> => queuePrinterOperation(async () => {
    await ensureQzConnected(useCertificates);
    return findPrinters();
});

/** Accept API response wrappers, Blob/ArrayBuffer, bytes, base64 or PDF data URLs. */
export const getPdfBlob = async (response: any): Promise<Blob> => {
    const content = response?.data ?? response;
    let blob: Blob;
    if (content instanceof Blob) {
        blob = content.slice(0, content.size, 'application/pdf');
    } else if (content instanceof ArrayBuffer || ArrayBuffer.isView(content) || Array.isArray(content)) {
        const bytes = Array.isArray(content) ? new Uint8Array(content) : content instanceof ArrayBuffer ? new Uint8Array(content) : new Uint8Array(content.buffer, content.byteOffset, content.byteLength);
        blob = new Blob([new Uint8Array(bytes)], { type: 'application/pdf' });
    } else if (typeof content === 'string' && content.trim()) {
        const base64 = content.trim().replace(/^data:application\/pdf;base64,/i, '').replace(/\s/g, '');
        let binary: string;
        try { binary = window.atob(base64); } catch { throw new Error('PDF content is not valid base64.'); }
        blob = new Blob([Uint8Array.from(binary, (char) => char.charCodeAt(0))], { type: 'application/pdf' });
    } else {
        throw new Error('PDF content is empty or unsupported.');
    }
    const header = await readBlob(blob.slice(0, 5), false);
    if (header !== '%PDF-') throw new Error('The response is not a PDF.');
    return blob;
};

const readBlob = (blob: Blob, base64: boolean): Promise<string> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
        if (typeof reader.result !== 'string') { reject(new Error('Could not read PDF.')); return; }
        resolve(base64 ? reader.result.substring(reader.result.indexOf(',') + 1) : reader.result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('Could not read PDF.'));
    reader.onabort = () => reject(new Error('Reading the PDF was cancelled.'));
    if (base64) reader.readAsDataURL(blob); else reader.readAsText(blob);
});

export const savePdfInBrowser = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename.replace(/[<>:"/\\\|?*\x00-\x1f]/g, '_').replace(/\.pdf$/i, '')}.pdf`;
    document.body.appendChild(link);
    try { link.click(); } finally {
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    }
};

export const getPageSize = (settings: PrinterSettings) => {
    switch (settings.pageSize) {
        case 'printer': return null;
        case 'a5': return { width: 148, height: 210 };
        case 'letter': return { width: 215.9, height: 279.4 };
        case 'label': return { width: 100, height: 150, custom: true };
        case 'custom': return { width: settings.widthMm, height: settings.heightMm, custom: true };
        default: return { width: 210, height: 297 };
    }
};

export type PrintOutcome = { method: 'printer' | 'savePdf' | 'skip'; printer?: string; reason?: string };

export const describePrintOutcome = (outcome: PrintOutcome) => {
    // if (outcome.method === 'printer') return `Sent to "${outcome.printer}".`;
    // const action = outcome.method === 'savePdf' ? 'PDF download requested.' : 'Printing skipped.';
    // return `${action}${outcome.reason ? ` ${outcome.reason}` : ''}`;
    return "";
};

export const printDispatchPdf = (content: unknown, settings: DispatchPrintingSettings,
    section: PrintingSection, filename: string, copies = 1): Promise<PrintOutcome> => queuePrinterOperation(async () => {
    if (!Number.isSafeInteger(copies) || copies < 1) throw new Error('Enter a positive whole number of labels.');
    const target = settings[section];
    if (!target.printer && settings.fallbackMethod === 'skip') return { method: 'skip', reason: 'No printer selected.' };
    const blob = await getPdfBlob(content);
    try {
        if (!target.printer) throw new Error('No printer selected.');
        const label = section === 'deliveryNote' || section === 'productLabel' || section === 'priceTag' ? settings[section] : null;
        const zebra = label?.mode === 'zebra';
        const options = zebra ? parseZebraOptions(label.zebraOptions) : {};
        const base64 = zebra && asBoolean(label.stretchToFit, section === 'priceTag') ? await stretchZebraPdf(blob, options) : await readBlob(blob, true);
        await ensureQzConnected(settings.useCertificates);
        const printers = await findPrinters();
        const printer = printers.find((name) => name.toLowerCase() === target.printer.toLowerCase());
        if (!printer) throw new Error(`Printer "${target.printer}" is unavailable.`);
        const config = qz.configs.create(printer, {
            copies, units: 'mm', size: zebra ? null : getPageSize(target), jobName: filename,
            ...(zebra ? { density: label.densityDpi / 25.4 } : {}),
        });
        // QZ rasterizes the PDF for a ZPL device. Setup commands belong inside
        // the label envelope; language/options belong on the PDF data item.
        // https\://qz.io/docs/raw#pdf-printing
        // Product quantity belongs to QZ copies; a saved ^PQ must not multiply it.
        const commands = label?.zebraCommands ?? '';
        const setup = (section === 'productLabel' || section === 'priceTag') ? commands.replace(/\^PQ[^\^\~]*/gi, '').trim() : commands;
        const data = zebra ? [
            `^XA\n${setup}\n`,
            { type: 'raw', format: 'pdf', flavor: 'base64', data: base64, options: { ...options, language: 'ZPL' } },
            (section === 'productLabel' || section === 'priceTag') ? '^PQ1\n^XZ\n' : '^XZ\n',
        ] : [{ type: 'pixel', format: 'pdf', flavor: 'base64', data: base64 }];
        await qz.print(config, data);
        return { method: 'printer', printer };
    } catch (error) {
        // Never resubmit to QZ: a rejected request may already have spooled.
        if (settings.fallbackMethod === 'skip') return { method: 'skip', reason: errorMessage(error) };
        savePdfInBrowser(blob, filename);
        return { method: 'savePdf', reason: errorMessage(error) };
    }
});

export type UploadPrintButtonProps = { settings?: DispatchPrintingSettings; disabled?: boolean; onBeforePrint?: () => void };

export const UploadPrintButton = ({ section, label, settings, disabled = false, onBeforePrint }: UploadPrintButtonProps & { section: PrintingSection; label: string }) => {
    const savedSettings = useDispatchPrintingSettings();
    const inputRef = useRef<HTMLInputElement>(null);
    const inProgress = useRef(false);
    const mounted = useRef(false);
    const [printing, setPrinting] = useState(false);
    const [result, setResult] = useState<{ severity: 'success' | 'warning' | 'error'; message: string } | null>(null);
    useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

    const uploadAndPrint = async (file: File) => {
        if (disabled || inProgress.current) return;
        inProgress.current = true;
        setPrinting(true);
        setResult(null);
        try {
            onBeforePrint?.();
            const printingSettings = settings ?? savedSettings;
            if (getIn(validateDispatchPrintingSettings({ DispatchPrinting: printingSettings }), `DispatchPrinting.${section}`)) throw new Error('Correct the printing settings for this document before printing.');
            const blob = await getPdfBlob(file);
            if (!mounted.current) return;
            const outcome = await printDispatchPdf(blob, printingSettings, section, file.name);
            const message = outcome.method === 'printer' ? `${file.name}: Sent to "${outcome.printer}".` : `${file.name}: ${outcome.method === 'savePdf' ? 'PDF download requested.' : 'Printing skipped.'}${outcome.reason ? ` ${outcome.reason}` : ''}`;
            if (mounted.current) setResult({ severity: outcome.method === 'printer' ? 'success' : 'warning', message });
        } catch (failure) {
            if (mounted.current) setResult({ severity: 'error', message: errorMessage(failure) });
        } finally {
            inProgress.current = false;
            if (mounted.current) setPrinting(false);
        }
    };

    return <>
        <Button type="button" variant="contained" startIcon={<PrintIcon />} disabled={disabled || printing} onClick={() => inputRef.current?.click()}>{printing ? 'Printing…' : label}</Button>
        <input ref={inputRef} type="file" accept="application/pdf,.pdf" hidden disabled={disabled || printing} onChange={(event) => { const file = event.target.files?.[0]; event.target.value = ''; if (file) void uploadAndPrint(file); }} />
        {/* <Snackbar open={result !== null} autoHideDuration={8000} onClose={(_, reason) => { if (reason !== 'clickaway') setResult(null); }}>
            {result ? <Alert severity={result.severity} onClose={() => setResult(null)}>{result.message}</Alert> : undefined}
        </Snackbar> */}
    </>;
};

export const UploadPickingListButton = (props: UploadPrintButtonProps) => <UploadPrintButton {...props} section="pickingList" label="Upload Picking List" />;
export const UploadRelocationOrderButton = (props: UploadPrintButtonProps) => <UploadPrintButton {...props} section="relocationOrder" label="Upload Relocation Order" />;
export const UploadDeliveryNoteButton = (props: UploadPrintButtonProps) => <UploadPrintButton {...props} section="deliveryNoteDocument" label="Upload Delivery Note" />;
export const UploadAddressLabelButton = (props: UploadPrintButtonProps) => <UploadPrintButton {...props} section="deliveryNote" label="Upload Address Label" />;
export const UploadProductLabelButton = (props: UploadPrintButtonProps) => <UploadPrintButton {...props} section="productLabel" label="Upload Product Label" />;
export const UploadPriceTagButton = (props: UploadPrintButtonProps) => <UploadPrintButton {...props} section="priceTag" label="Upload Price Tag" />;

export const getDispatchResultData = (result: any) => {
    const data = result?.data ?? result;
    return data?.DispatchOrder?.DispatchOrderId != null ? data : null;
};

export const getDispatchPrintJobs = (data: any) => {
    const jobs: { name: string; content: unknown; section: 'deliveryNoteDocument' | 'deliveryNote' }[] = [];
    const deliveryNoteContents = (data?.DeliveryNoteContent == null ? [] : Array.isArray(data.DeliveryNoteContent) ? data.DeliveryNoteContent : [data.DeliveryNoteContent])
        .filter((content) => content != null && (typeof content !== 'string' || content.trim() !== ''));
    deliveryNoteContents.forEach((content, index) => {
        jobs.push({ name: deliveryNoteContents.length > 1 ? `Delivery note ${index + 1}` : 'Delivery note', content, section: 'deliveryNoteDocument' });
    });
    if (Array.isArray(data?.AddressLabelsContent)) data.AddressLabelsContent.forEach((content, index) => {
        jobs.push({ name: `Address label ${index + 1}`, content, section: 'deliveryNote' });
    });
    return jobs;
};

export const useDispatchResultPrinting = (data: any, settings: DispatchPrintingSettings) => {
    const [printing, setPrinting] = useState(false);
    const [printErrors, setPrintErrors] = useState<string[]>([]);
    const [printNotices, setPrintNotices] = useState<string[]>([]);
    const handledShipments = useRef(new Set<string>());
    const handledResponses = useRef(new WeakSet<object>());
    const printQueue = useRef<Promise<void>>(Promise.resolve());
    const mounted = useRef(false);
    useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

    useEffect(() => {
        if (!data) return;
        const shipmentId = data.ShipmentDetails?.ShipmentId;
        const shipmentKey = shipmentId != null ? `${data.DispatchOrder.DispatchOrderId}:${shipmentId}` : null;
        if (handledResponses.current.has(data) || (shipmentKey && handledShipments.current.has(shipmentKey))) return;
        // Claim synchronously, before queueing, so re-renders/settings changes
        // cannot reprint an old response or dispatch the order a second time.
        handledResponses.current.add(data);
        if (shipmentKey) handledShipments.current.add(shipmentKey);
        const jobs = getDispatchPrintJobs(data);
        if (!jobs.length) return;
        printQueue.current = printQueue.current.then(async () => {
            if (!mounted.current) return;
            setPrinting(true);
            try {
                for (const job of jobs) {
                    if (!mounted.current) break;
                    const name = `Order ${data.DispatchOrder.DispatchOrderId} - ${job.name}`;
                    try {
                        const outcome = await printDispatchPdf(job.content, settings, job.section, name);
                        // if (mounted.current && outcome.method !== 'printer') setPrintNotices((current) => [...current, `${name}: ${describePrintOutcome(outcome)}`]);
                    } catch (error) {
                        if (mounted.current) setPrintErrors((current) => [...current, `${name}: ${errorMessage(error)}`]);
                    }
                }
            } finally { if (mounted.current) setPrinting(false); }
        });
    }, [data, settings]);

    return { printing, printErrors, printNotices };
};

export type PickingListPrintResult = { outcome?: PrintOutcome; error?: string };
export type ProductLabelPrintResult = PickingListPrintResult;

/** Mount once per receipt/reprint, with a unique key even for the same product. */
export const ProductLabelPrintJob = ({ productId, copies, settings, onComplete }: {
    productId: string | number; copies: number; settings: DispatchPrintingSettings;
    onComplete: (result: ProductLabelPrintResult) => void;
}) => {
    const { rows, loading, status } = usePrintProductLabel(productId);
    const claimed = useRef(false);
    const mounted = useRef(false);
    const completeRef = useRef(onComplete);
    completeRef.current = onComplete;
    useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
    useEffect(() => {
        if (claimed.current || loading) return;
        const failed = status === 'error' || status === 'aborted' || status === 204 || (typeof status === 'number' && status >= 400);
        const succeeded = status === 'success' || (typeof status === 'number' && status >= 200 && status < 300);
        const empty = rows == null || (Array.isArray(rows) && rows.length === 0);
        if (empty && !failed && !succeeded) return;
        claimed.current = true;
        if (failed || empty) {
            completeRef.current({ error: `Could not load the product label for product ${productId} (${status}).` });
            return;
        }
        const content = Array.isArray(rows) && rows.length === 1 && typeof rows[0] !== 'number' ? rows[0] : rows;
        printDispatchPdf(content, settings, 'productLabel', `Product label - ${productId}`, copies)
            .then((outcome) => { if (mounted.current) completeRef.current({ outcome }); })
            .catch((error) => { if (mounted.current) completeRef.current({ error: errorMessage(error) }); });
    }, [productId, copies, rows, loading, status, settings]);
    return null;
};

/** Mount with a new key for every request, including manual reprints. This
 * prevents useResponse's previous Blob from being printed for a new order. */
export const PickingListPrintJob = ({ orderId, warehouseId, settings, onComplete }: {
    orderId: number | string; warehouseId: number | string; settings: DispatchPrintingSettings;
    onComplete: (result: PickingListPrintResult) => void;
}) => {
    const { rows, loading, status } = useGetPickingListByDispatchOrder(orderId, warehouseId);
    const claimed = useRef(false);
    const mounted = useRef(false);
    const completeRef = useRef(onComplete);
    completeRef.current = onComplete;
    useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
    useEffect(() => {
        if (claimed.current || loading) return;
        const failed = status === 'error' || status === 'aborted' || status === 204 || (typeof status === 'number' && status >= 400);
        if (rows == null && !failed && status !== 'success') return;
        claimed.current = true;
        if (failed || rows == null) {
            completeRef.current({ error: `Could not load the picking list for order ${orderId} (${status}).` });
            return;
        }
        printDispatchPdf(rows, settings, 'pickingList', `Picking list - ${orderId}`)
            .then((outcome) => { if (mounted.current) completeRef.current({ outcome }); })
            .catch((error) => { if (mounted.current) completeRef.current({ error: errorMessage(error) }); });
    }, [orderId, rows, loading, status, settings]);
    return null;
};

/** Mount with a new key for each collective print request. */
export const CollectivePickingListPrintJob = ({ pickingListIds, warehouseId, settings, onComplete }: {
    pickingListIds: string; warehouseId: number | string; settings: DispatchPrintingSettings;
    onComplete: (result: PickingListPrintResult) => void;
}) => {
    const { rows, loading, status } = useGetCollectivePickingList(pickingListIds, warehouseId);
    const claimed = useRef(false);
    const mounted = useRef(false);
    const completeRef = useRef(onComplete);
    completeRef.current = onComplete;

    useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

    useEffect(() => {
        if (claimed.current || loading) return;
        const failed = status === 'error' || status === 'aborted' || status === 204 || (typeof status === 'number' && status >= 400);
        if (rows == null && !failed && status !== 'success') return;

        claimed.current = true;
        if (failed || rows == null) {
            completeRef.current({ error: `Could not load the collective picking list (${status}).` });
            return;
        }

        printDispatchPdf(rows, settings, 'pickingList', 'Collective picking list')
            .then((outcome) => { if (mounted.current) completeRef.current({ outcome }); })
            .catch((error) => { if (mounted.current) completeRef.current({ error: errorMessage(error) }); });
    }, [rows, loading, status, settings]);

    return null;
};

export const DispatchPrintTimingSettings = ({ value, onChange }: {
    value: DispatchPrintingSettings['pickingList']; onChange: (field: 'delayMs' | 'refreshMs', value: number) => void;
}) => (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TimingField label="Printer delay (seconds)" value={value.delayMs / 1000} min={0} max={3600} onChange={(number) => onChange('delayMs', Math.round(number * 1000))} />
        <TimingField label="Refresh interval (seconds)" value={value.refreshMs / 1000} min={1} max={86400} onChange={(number) => onChange('refreshMs', Math.round(number * 1000))} />
    </Box>
);

const TimingField = ({ label, value, min, max, onChange }) => {
    const [draft, setDraft] = useState(String(value));
    useEffect(() => { setDraft(String(value)); }, [value]);
    const invalid = draft === '' || !Number.isFinite(Number(draft)) || Number(draft) < min || Number(draft) > max;
    return <TextField label={label} type="number" size="small" value={draft} inputProps={{ min, max, step: 0.1 }} error={invalid} helperText={invalid ? `Enter ${min}–${max} seconds. The previous value is still active.` : 'Applied when you leave this field.'} onChange={(event) => setDraft(event.target.value)} onBlur={() => { if (!invalid) onChange(Number(draft)); }} />;
};

export const DispatchPrintingSettingsCard = ({ formik }: { formik: FormikProps<any> }) => {
    const scannerConfiguration = useScannerConfiguration();
    const value = getDispatchPrintingSettings(formik.values);
    const [printers, setPrinters] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [loaded, setLoaded] = useState(false);
    const [testPrinting, setTestPrinting] = useState<PrintingSection | null>(null);
    const [testResult, setTestResult] = useState<{ severity: 'success' | 'warning' | 'error'; message: string } | null>(null);
    const testInProgress = useRef(false);
    const requestId = useRef(0);
    const mounted = useRef(false);
    useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
    const refresh = useCallback(async () => {
        const id = ++requestId.current;
        setLoading(true);
        setError('');
        try {
            const names = await loadDispatchPrinters(value.useCertificates);
            if (mounted.current && id === requestId.current) { setPrinters(names); setLoaded(true); }
        } catch (failure) {
            if (mounted.current && id === requestId.current) { setError(errorMessage(failure)); setLoaded(false); }
        } finally { if (mounted.current && id === requestId.current) setLoading(false); }
    }, [value.useCertificates]);
    useEffect(() => { refresh(); }, [refresh]);

    const testPrint = async (section: PrintingSection, pdfUrl: URL) => {
        if (testInProgress.current) return;
        testInProgress.current = true;
        setTestPrinting(section);
        setTestResult(null);
        try {
            const validation = validateDispatchPrintingSettings(formik.values);
            if (getIn(validation, `DispatchPrinting.${section}`)) throw new Error('Correct the printing settings for this document before test printing.');
            const response = await fetch(pdfUrl, { cache: 'no-store' });
            if (!response.ok) throw new Error(`Could not load the test PDF (${response.status}).`);
            const filename = section === 'relocationOrder' ? 'Relocation order test' : section === 'pickingList' ? 'Picking list test' : section === 'deliveryNoteDocument' ? 'Delivery note test' : section === 'deliveryNote' ? 'Address label test' : section === 'priceTag' ? 'Price tag test' : 'Product label test';
            const outcome = await printDispatchPdf(await response.blob(), getDispatchPrintingSettings(formik.values), section, filename);
            const message = outcome.method === 'printer' ? `${filename}: Sent to "${outcome.printer}".` : `${filename}: ${outcome.method === 'savePdf' ? 'PDF download requested.' : 'Printing skipped.'}${outcome.reason ? ` ${outcome.reason}` : ''}`;
            if (mounted.current) setTestResult({ severity: outcome.method === 'printer' ? 'success' : 'warning', message });
        } catch (failure) {
            if (mounted.current) setTestResult({ severity: 'error', message: errorMessage(failure) });
        } finally {
            testInProgress.current = false;
            if (mounted.current) setTestPrinting(null);
        }
    };

    const uploadProps = (section: PrintingSection): UploadPrintButtonProps => ({
        settings: value, disabled: testPrinting !== null,
        onBeforePrint: () => {
            if (getIn(validateDispatchPrintingSettings(formik.values), `DispatchPrinting.${section}`)) throw new Error('Correct the printing settings for this document before printing.');
        },
    });

    const field = (path: string, label: string, extra: any = {}) => {
        const name = `DispatchPrinting.${path}`;
        const error = getIn(formik.errors, name);
        const onChange = (event: { target: { value: string } }) => {
            const [section, key] = path.split('.');
            if (key && (section === 'deliveryNoteDocument' || section === 'relocationOrder' || section === 'deliveryNote' || section === 'productLabel' || section === 'priceTag')) formik.setFieldValue(`DispatchPrinting.${section}`, { ...value[section], ...formik.values.DispatchPrinting?.[section], [key]: event.target.value });
            else formik.handleChange(event);
        };
        return <TextField fullWidth size="small" name={name} label={label} value={getIn(formik.values, name) ?? getIn({ DispatchPrinting: value }, name) ?? ''} onChange={onChange} onBlur={formik.handleBlur} error={Boolean(error)} helperText={error} {...extra} />;
    };
    const printerFields = (section: PrintingSection) => {
        const selected = value[section].printer;
        const isLabel = section === 'deliveryNote' || section === 'productLabel' || section === 'priceTag';
        const zebra = (section === 'deliveryNote' || section === 'productLabel' || section === 'priceTag') && value[section].mode === 'zebra';
        return <>
            {field(`${section}.printer`, 'Printer', { select: true, children: [
                <MenuItem key="fallback" value="">Use fallback method</MenuItem>,
                ...(selected && !printers.includes(selected) ? [<MenuItem key={selected} value={selected}>{selected} (unavailable)</MenuItem>] : []),
                ...printers.map((name) => <MenuItem key={name} value={name}>{name}</MenuItem>),
            ] })}
            {isLabel && field(`${section}.mode`, 'Printer type', { select: true, onChange: (event: { target: { value: string } }) => {
                const mode = event.target.value;
                formik.setFieldValue(`DispatchPrinting.${section}`, { ...value[section], ...formik.values.DispatchPrinting?.[section], mode });
            }, children: [
                <MenuItem key="pdf" value="pdf">Standard printer (PDF)</MenuItem>,
                <MenuItem key="zebra" value="zebra">Zebra (ZPL)</MenuItem>,
            ] })}
            {!zebra && field(`${section}.pageSize`, 'Page size', { select: true, children: [
                ['printer', 'Printer default'], ['a4', 'A4 (210 × 297 mm)'], ['a5', 'A5 (148 × 210 mm)'],
                ['letter', 'Letter (215.9 × 279.4 mm)'], ['label', 'Label (100 × 150 mm)'], ['custom', 'Custom'],
            ].map(([key, label]) => <MenuItem key={key} value={key}>{label}</MenuItem>) })}
            {!zebra && value[section].pageSize === 'custom' && <Box sx={{ display: 'flex', gap: 2 }}>
                {field(`${section}.widthMm`, 'Width (mm)', { type: 'number', inputProps: { min: 1, max: 2000 } })}
                {field(`${section}.heightMm`, 'Height (mm)', { type: 'number', inputProps: { min: 1, max: 2000 } })}
            </Box>}
        </>;
    };
    const zebraFields = (section: 'deliveryNote' | 'productLabel' | 'priceTag') => value[section].mode === 'zebra' && <>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>{field(`${section}.densityDpi`, 'Printer resolution (DPI)', { type: 'number', inputProps: { min: 1, max: 2400 } })}</Box>
            <FormControlLabel sx={{ m: 0, flexShrink: 0 }} label="Apply streching" control={<Checkbox checked={value[section].stretchToFit} onChange={(_, checked) => formik.setFieldValue(`DispatchPrinting.${section}`, { ...value[section], ...formik.values.DispatchPrinting?.[section], stretchToFit: checked })} />} />
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>{field(`${section}.zebraOptions`, 'Zebra PDF options (JSON)', { multiline: true, minRows: 2, placeholder: DEFAULT_DISPATCH_PRINTING[section].zebraOptions })}</Box>
            <Button type="button" size="small" variant="outlined" onClick={() => formik.setFieldValue(`DispatchPrinting.${section}`, { ...value[section], ...formik.values.DispatchPrinting?.[section], zebraOptions: DEFAULT_DISPATCH_PRINTING[section].zebraOptions })}>Use Defaults</Button>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>{field(`${section}.zebraCommands`, 'Zebra setup commands', { multiline: true, minRows: 2, placeholder: DEFAULT_DISPATCH_PRINTING[section].zebraCommands })}</Box>
            <Button type="button" size="small" variant="outlined" onClick={() => formik.setFieldValue(`DispatchPrinting.${section}`, { ...value[section], ...formik.values.DispatchPrinting?.[section], zebraCommands: DEFAULT_DISPATCH_PRINTING[section].zebraCommands })}>Use Defaults</Button>
        </Box>
    </>;
    return <>
        <ScannerDialog {...scannerConfiguration.dialogProps} />
        <ContentCard cardId="Warehouse settings" cardTypes={[CardType.collapsable, CardType.visibleInitally]} components={[
            {component:<ScannerButton onClick={scannerConfiguration.openDialog} />},

        ]}>
        <Box sx={{ width: 1 }}>
            <SettingContainer>
                <SettingsLabel info="The warehouse used on warehouse pages, including dispatch orders" />
                <Grid item xs={12} md={4.8}>
                    <WarehouseSelector label="My selected warehouse" value={formik.values[ACTIVE_WAREHOUSE_SETTING] ?? null} onChange={(warehouseId) => formik.setFieldValue(ACTIVE_WAREHOUSE_SETTING, warehouseId)} />
                </Grid>
            </SettingContainer>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
                <Typography variant="h2" sx={{ flexGrow: 1 }}>Printer settings</Typography>

            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
                <Box sx={{ minWidth: 260 }}>{field('fallbackMethod', 'Fallback method', { select: true, children: [
                    <MenuItem key="savePdf" value="savePdf">Save as PDF via browser</MenuItem>,
                    <MenuItem key="skip" value="skip">Skip printing</MenuItem>,
                ] })}</Box>
                <FormControlLabel label="Use QZ certificates" control={<Switch checked={value.useCertificates} onChange={(_, checked) => formik.setFieldValue('DispatchPrinting.useCertificates', checked)} />} />
                <Button type="button" variant="outlined" disabled={loading} onClick={refresh}>{loading ? 'Loading printers…' : 'Refresh printers'}</Button>
            </Box>
            {/* <Typography variant="body2" sx={{ mb: 2 }}>When no printer is selected or QZ printing fails, save the PDF as a browser download or skip it. Printer choices come from QZ Tray on this computer.</Typography> */}
            {!value.useCertificates && <Alert severity="info" sx={{ mb: 2 }}>QZ Tray will ask you to allow access and printing. Keep the page open and approve its prompts.</Alert>}
            {error && <Alert severity="warning" sx={{ mb: 2 }}>Could not load printers: {error} You can still save settings and use the fallback method.</Alert>}
            {testResult && <Alert severity={testResult.severity} sx={{ mb: 2 }}>{testResult.message}</Alert>}
            {loaded && !printers.length && <Alert severity="info" sx={{ mb: 2 }}>QZ Tray did not report any printers.</Alert>}
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                            <Typography variant="h3">Picking list</Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Button type="button" variant="contained" startIcon={<PrintIcon />} disabled={testPrinting !== null} onClick={() => testPrint('pickingList', new URL('./picking-list-example.pdf', import.meta.url))}>{testPrinting === 'pickingList' ? 'Printing…' : 'Test picking list'}</Button>
                                <UploadPickingListButton {...uploadProps('pickingList')} />
                            </Box>
                        </Box>
                        {printerFields('pickingList')}
                        <DispatchPrintTimingSettings value={value.pickingList} onChange={(key, number) => formik.setFieldValue(`DispatchPrinting.pickingList.${key}`, number)} />
                        <Divider sx={{ my: 1 }} />
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                            <Typography variant="h3">Delivery Note</Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Button type="button" variant="contained" startIcon={<PrintIcon />} disabled={testPrinting !== null} onClick={() => testPrint('deliveryNoteDocument', new URL('./picking-list-example.pdf', import.meta.url))}>{testPrinting === 'deliveryNoteDocument' ? 'Printing…' : 'Test Delivery Note'}</Button>
                                <UploadDeliveryNoteButton {...uploadProps('deliveryNoteDocument')} />
                            </Box>
                        </Box>
                        {printerFields('deliveryNoteDocument')}
                        <Divider sx={{ my: 1 }} />
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                            <Typography variant="h3">Relocation orders</Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Button type="button" variant="contained" startIcon={<PrintIcon />} disabled={testPrinting !== null} onClick={() => testPrint('relocationOrder', new URL('./relocation-order-example.pdf', import.meta.url))}>{testPrinting === 'relocationOrder' ? 'Printing…' : 'Test Relocation Order'}</Button>
                                <UploadRelocationOrderButton {...uploadProps('relocationOrder')} />
                            </Box>
                        </Box>
                        {printerFields('relocationOrder')}
                    </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                            <Typography variant="h3">Address labels</Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Button type="button" variant="contained" startIcon={<PrintIcon />} disabled={testPrinting !== null} onClick={() => testPrint('deliveryNote', new URL('./address-label-example-2.pdf', import.meta.url))}>{testPrinting === 'deliveryNote' ? 'Printing…' : 'Test Address Label'}</Button>
                                <UploadAddressLabelButton {...uploadProps('deliveryNote')} />
                            </Box>
                        </Box>
                        {printerFields('deliveryNote')}
                        {zebraFields('deliveryNote')}
                        <Divider sx={{ my: 1 }} />
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                            <Typography variant="h3">Product labels</Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Button type="button" variant="contained" startIcon={<PrintIcon />} disabled={testPrinting !== null} onClick={() => testPrint('productLabel', new URL('./product-label-example.pdf', import.meta.url))}>{testPrinting === 'productLabel' ? 'Printing…' : 'Test Product Label'}</Button>
                                <UploadProductLabelButton {...uploadProps('productLabel')} />
                            </Box>
                        </Box>
                        {printerFields('productLabel')}
                        {zebraFields('productLabel')}
                        <Divider sx={{ my: 1 }} />
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                            <Typography variant="h3">Price tags</Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Button type="button" variant="contained" startIcon={<PrintIcon />} disabled={testPrinting !== null} onClick={() => testPrint('priceTag', new URL('./price-tag-example.pdf', import.meta.url))}>{testPrinting === 'priceTag' ? 'Printing…' : 'Test Price Tag'}</Button>
                                <UploadPriceTagButton {...uploadProps('priceTag')} />
                            </Box>
                        </Box>
                        {printerFields('priceTag')}
                        {zebraFields('priceTag')}
                    </Box>
                </Grid>
            </Grid>
            <Divider sx={{ my: 2 }} />
            {/* <Typography variant="caption">Save this page to apply changes. Delay and refresh can also be adjusted on the automatic printing page.</Typography> */}
        </Box>
    </ContentCard>
    </>;
};
